import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import {
  EvaluationContentLibraryService,
  FormatTypeService,
  ManagePersonaService,
  ObservationService,
  RecommendationService,
  StandardEvaluationTypeService,
  TableManagementService,
} from 'generated-api';
import { SessionService } from '../services/session.service';
import { EDIT_ACCESS_DENIED_QUERY_PARAM } from '../utils/edit-access-denied-alert';

const DENIED_QP = { [EDIT_ACCESS_DENIED_QUERY_PARAM]: '1' };

async function ensureSessionLoaded(
  sessionService: SessionService,
  router: Router
): Promise<boolean> {
  if (!sessionService.isSessionLoaded()) {
    try {
      await firstValueFrom(
        sessionService.checkSessionStatus().pipe(timeout(8000))
      );
    } catch {
      await router.navigate(['/']);
      return false;
    }
  }
  return true;
}

/** `/evaluation-type/:evaluationTypeId` is edit-only; redirect if API says user cannot edit. */
export const standardEvaluationTypeEditGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const api = inject(StandardEvaluationTypeService);
  const sessionService = inject(SessionService);

  if (!(await ensureSessionLoaded(sessionService, router))) {
    return false;
  }
  const id = route.paramMap.get('evaluationTypeId');
  if (!id) {
    return router.createUrlTree(['/settings/evaluation-types'], {
      queryParams: DENIED_QP,
    });
  }
  try {
    const item = await firstValueFrom(api.standardEvaluationTypeGetById(id, true));
    if (item.canEdit !== true) {
      return router.createUrlTree(['/settings/evaluation-types'], {
        queryParams: DENIED_QP,
      });
    }
    return true;
  } catch {
    return router.createUrlTree(['/settings/evaluation-types'], {
      queryParams: DENIED_QP,
    });
  }
};

/** `/format-type/:formatTypeId` — redirect when not editable. */
export const formatTypeEditGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const api = inject(FormatTypeService);
  const sessionService = inject(SessionService);

  if (!(await ensureSessionLoaded(sessionService, router))) {
    return false;
  }
  const id = route.paramMap.get('formatTypeId');
  if (!id) {
    return router.createUrlTree(['/settings/format-types'], {
      queryParams: DENIED_QP,
    });
  }
  try {
    const item = await firstValueFrom(api.getFormatType(id, true));
    if (item.canEdit !== true) {
      return router.createUrlTree(['/settings/format-types'], {
        queryParams: DENIED_QP,
      });
    }
    return true;
  } catch {
    return router.createUrlTree(['/settings/format-types'], {
      queryParams: DENIED_QP,
    });
  }
};

/** `/settings/recommendation/:recommendationId` */
export const recommendationEditGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const api = inject(RecommendationService);
  const sessionService = inject(SessionService);

  if (!(await ensureSessionLoaded(sessionService, router))) {
    return false;
  }
  const id = route.paramMap.get('recommendationId');
  if (!id) {
    return router.createUrlTree(['/settings/recommendations'], {
      queryParams: DENIED_QP,
    });
  }
  try {
    const res = await firstValueFrom(api.recommendationGetById(id));
    const rec = res.recommendation;
    // GET by id does not always include canEdit; rely on successful load for route access (detail page enforces read-only).
    if (!rec?.pkId) {
      return router.createUrlTree(['/settings/recommendations'], {
        queryParams: DENIED_QP,
      });
    }
    return true;
  } catch {
    return router.createUrlTree(['/settings/recommendations'], {
      queryParams: DENIED_QP,
    });
  }
};

/** `/settings/behavioral-observations/edit/:id` */
export const observationEditGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const api = inject(ObservationService);
  const sessionService = inject(SessionService);

  if (!(await ensureSessionLoaded(sessionService, router))) {
    return false;
  }
  const id = route.paramMap.get('id');
  if (!id) {
    return router.createUrlTree(['/settings/behavioral-observations'], {
      queryParams: DENIED_QP,
    });
  }
  try {
    const item = await firstValueFrom(api.getObservation(id));
    if (!item?.pkId) {
      return router.createUrlTree(['/settings/behavioral-observations'], {
        queryParams: DENIED_QP,
      });
    }
    return true;
  } catch {
    return router.createUrlTree(['/settings/behavioral-observations'], {
      queryParams: DENIED_QP,
    });
  }
};

/** `/settings/content-library/edit/:id` */
export const evaluationContentLibraryEditGuard: CanActivateFn = async (
  route
) => {
  const router = inject(Router);
  const api = inject(EvaluationContentLibraryService);
  const sessionService = inject(SessionService);

  if (!(await ensureSessionLoaded(sessionService, router))) {
    return false;
  }
  const id = route.paramMap.get('id');
  if (!id) {
    return router.createUrlTree(['/settings/content-library'], {
      queryParams: DENIED_QP,
    });
  }
  try {
    const item = await firstValueFrom(api.get(id));
    if (item.canEdit !== true) {
      return router.createUrlTree(['/settings/content-library'], {
        queryParams: DENIED_QP,
      });
    }
    return true;
  } catch {
    return router.createUrlTree(['/settings/content-library'], {
      queryParams: DENIED_QP,
    });
  }
};

/** `/settings/edit-table/:id` — `new` is create; otherwise GET and require canEdit. */
export const evaluationReportTableEditGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const api = inject(TableManagementService);
  const sessionService = inject(SessionService);

  if (!(await ensureSessionLoaded(sessionService, router))) {
    return false;
  }
  const id = route.paramMap.get('id');
  if (!id) {
    return router.createUrlTree(['/settings/tables'], { queryParams: DENIED_QP });
  }
  const normalized = id.trim().toLowerCase();
  if (normalized === 'new' || normalized === '') {
    return true;
  }
  try {
    const item = await firstValueFrom(api.getEvaluationReportTable(id));
    if (item.canEdit !== true) {
      return router.createUrlTree(['/settings/tables'], {
        queryParams: DENIED_QP,
      });
    }
    return true;
  } catch {
    return router.createUrlTree(['/settings/tables'], { queryParams: DENIED_QP });
  }
};

const PERSONA_CREATE_IDS = new Set(['new-persona', 'new', '']);

/** `/personas/upsert/:id` — allow create placeholders; otherwise require canEdit on GET. */
export const personaUpsertEditGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const api = inject(ManagePersonaService);
  const sessionService = inject(SessionService);

  if (!(await ensureSessionLoaded(sessionService, router))) {
    return false;
  }
  const id = route.paramMap.get('id');
  if (!id || PERSONA_CREATE_IDS.has(id)) {
    return true;
  }
  try {
    const item = await firstValueFrom(api.getPersona(id));
    if (item.canEdit !== true) {
      return router.createUrlTree(['/settings/personas'], {
        queryParams: DENIED_QP,
      });
    }
    return true;
  } catch {
    return router.createUrlTree(['/settings/personas'], { queryParams: DENIED_QP });
  }
};
